import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileSpreadsheet, ClipboardPaste, Download } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";
import { toast } from "sonner";

interface ImportMethodSelectorProps {
  onFileSelected: (file: File) => void;
  onTextPasted: (text: string) => void;
}

export function ImportMethodSelector({ onFileSelected, onTextPasted }: ImportMethodSelectorProps) {
  const [pastedText, setPastedText] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["Nome Completo*", "CPF*", "Data Nascimento", "Sexo", "Email", "Celular"],
      ["João Silva", "12345678900", "1980-01-15", "M", "joao@email.com", "11999999999"],
      ["Maria Santos", "98765432100", "1990-03-20", "F", "maria@email.com", "11988888888"]
    ]);
    
    XLSX.utils.book_append_sheet(wb, ws, "Pacientes");
    XLSX.writeFile(wb, "modelo_importacao_pacientes.xlsx");
    toast.success("Modelo XLSX baixado com sucesso");
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const xlsxFile = files.find(f => f.name.match(/\.(xlsx|xls)$/i));
    
    if (!xlsxFile) {
      toast.error("Apenas arquivos XLSX são permitidos");
      return;
    }

    onFileSelected(xlsxFile);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Apenas arquivos XLSX são permitidos");
      return;
    }

    onFileSelected(file);
  };

  const handlePaste = () => {
    if (!pastedText.trim()) {
      toast.error("Cole os dados antes de continuar");
      return;
    }
    onTextPasted(pastedText);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Selecione o Método de Importação</h2>
          <p className="text-muted-foreground">
            Escolha como deseja importar seus pacientes
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} className="gap-2">
          <Download className="h-4 w-4" />
          Baixar Modelo XLSX
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Upload Card */}
        <Card 
          className={cn(
            "hover:shadow-lg transition-all cursor-pointer border-2",
            dragActive && "border-primary bg-primary/5"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <CardHeader className="bg-gradient-to-br from-primary/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileSpreadsheet className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Upload de Arquivo</CardTitle>
                <CardDescription>Arraste ou selecione arquivo XLSX</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                dragActive ? "border-primary bg-primary/5" : "border-border"
              )}
            >
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                Arraste um arquivo XLSX aqui
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                ou clique no botão abaixo
              </p>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileInput}
              />
              <label htmlFor="file-upload">
                <Button variant="outline" asChild>
                  <span className="gap-2">
                    <Upload className="h-4 w-4" />
                    Selecionar Arquivo XLSX
                  </span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Máximo 10MB • Apenas .xlsx ou .xls
            </p>
          </CardContent>
        </Card>

        {/* Paste Card */}
        <Card className="hover:shadow-lg transition-all border-2">
          <CardHeader className="bg-gradient-to-br from-secondary/5 to-transparent">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-secondary/10 rounded-lg">
                <ClipboardPaste className="h-6 w-6 text-secondary-foreground" />
              </div>
              <div>
                <CardTitle>Colar Dados</CardTitle>
                <CardDescription>Cole dados do Excel ou planilha</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Cole aqui os dados dos pacientes...&#10;&#10;Exemplo:&#10;Nome          CPF             Data Nasc   Sexo&#10;João Silva    123.456.789-00  15/01/1980  M&#10;Maria Santos  987.654.321-00  20/03/1990  F"
              className="min-h-[200px] font-mono text-sm"
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
            />
            <Button 
              onClick={handlePaste} 
              className="w-full gap-2"
              disabled={!pastedText.trim()}
            >
              <ClipboardPaste className="h-4 w-4" />
              Processar Dados Colados
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              A IA detectará automaticamente as colunas
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
